import { useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormHelperText from '@mui/material/FormHelperText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  isSupportedVideoLink,
  VIDEO_MISSING_SOURCE_MSG,
  VIDEO_UNSUPPORTED_LINK_MSG,
  validateVideoUploadFile,
} from '@/features/instruments/domain/videoSource'
import videoUrlInfoIcon from '@/assets/instruments/question-builder/video-url-info.svg'
import { palettePrimaryMain } from '@/theme/designTokens'
import {
  gateFieldError,
  useShowBuilderSaveValidation,
} from './builderSaveValidationContext'
import type { BuilderQuestion, VideoConfig, VideoSourceKind } from './types'
import { VideoPlaybackEmbed } from './VideoPlaybackEmbed'
import { uploadBuilderQuestionVideo } from './uploadQuestionVideo'

const SECTION_LABEL_SX = {
  display: 'block',
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: 700,
  letterSpacing: '-0.15px',
  color: 'text.primary',
  mb: 0,
} as const

const HELPER_SX = {
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '-0.15px',
  color: '#6b7a90',
} as const

const OUTLINED_SX = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    minHeight: 46,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  },
  '& .MuiOutlinedInput-input': {
    py: '12px',
    px: '16px',
    fontSize: '14px',
  },
} as const

const PREVIEW_CARD_SX = {
  border: '1px solid #e1e7f3',
  borderRadius: '10px',
  bgcolor: '#fff',
  p: '25px',
} as const

type Props = Readonly<{
  q: BuilderQuestion
  onPatch: (patch: { config: BuilderQuestion['config'] }) => void
  campaignId?: string
  instrumentId?: string
}>

function hasConfiguredSource(d: VideoConfig): boolean {
  return d.videoUrl.trim().length > 0
}

export function VideoQuestionSection({ q, onPatch, campaignId = '', instrumentId = '' }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const urlId = useId()
  const showSaveValidation = useShowBuilderSaveValidation()
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [pendingKind, setPendingKind] = useState<VideoSourceKind | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [replaceOpen, setReplaceOpen] = useState(false)

  if (q.config.type !== 'VIDEO') return null
  const d = q.config.data

  const setData = (patch: Partial<VideoConfig>) =>
    onPatch({ config: { type: 'VIDEO', data: { ...d, ...patch } } })

  const urlTrim = d.videoUrl.trim()
  const linkInvalid = Boolean(urlTrim && d.sourceKind === 'link' && !isSupportedVideoLink(urlTrim))
  const missingSource = !urlTrim
  let sourceMessage: string | null = null
  if (missingSource) sourceMessage = VIDEO_MISSING_SOURCE_MSG
  else if (linkInvalid) sourceMessage = VIDEO_UNSUPPORTED_LINK_MSG
  const sourceErr = gateFieldError(showSaveValidation, sourceMessage)

  const applySourceKind = (kind: VideoSourceKind) => {
    setUploadError(null)
    if (kind === d.sourceKind) return
    if (hasConfiguredSource(d)) {
      setPendingKind(kind)
      setPendingFile(null)
      setReplaceOpen(true)
      return
    }
    setData({
      sourceKind: kind,
      videoUrl: '',
      uploadedFileName: '',
    })
  }

  const confirmReplace = () => {
    const kind = pendingKind
    const file = pendingFile
    setPendingKind(null)
    setPendingFile(null)
    setReplaceOpen(false)
    setUploadError(null)
    if (kind === 'upload' && file) {
      void runUpload(file)
      return
    }
    if (kind) {
      setData({
        sourceKind: kind,
        videoUrl: '',
        uploadedFileName: '',
      })
    }
  }

  const onLinkChange = (next: string) => {
    if (d.sourceKind === 'upload' && hasConfiguredSource(d) && next.trim()) {
      setPendingKind('link')
      setReplaceOpen(true)
      return
    }
    setData({ sourceKind: 'link', videoUrl: next, uploadedFileName: '' })
  }

  const onPickFile = async (file: File | undefined) => {
    if (!file) return
    const fileErr = validateVideoUploadFile(file)
    if (fileErr) {
      setUploadError(fileErr)
      return
    }
    if (hasConfiguredSource(d)) {
      setPendingKind('upload')
      setPendingFile(file)
      setReplaceOpen(true)
      return
    }
    await runUpload(file)
  }

  const runUpload = async (file: File) => {
    setUploadError(null)
    if (!instrumentId) {
      setUploadError('Save the instrument draft before uploading a video.')
      return
    }
    setIsUploading(true)
    try {
      const videoUrl = await uploadBuilderQuestionVideo({
        campaignId,
        instrumentId,
        questionId: q.id,
        file,
      })
      setData({
        sourceKind: 'upload',
        videoUrl,
        uploadedFileName: file.name,
      })
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Video upload failed')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const tabSx = (active: boolean) => ({
    flex: 1,
    textTransform: 'none' as const,
    borderRadius: 0,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19.5px',
    letterSpacing: '-0.08px',
    minHeight: 36,
    boxShadow: 'none',
    color: active ? '#fff' : '#6b7a90',
    bgcolor: active ? 'primary.main' : '#fff',
    '&:hover': {
      bgcolor: active ? 'primary.main' : 'action.hover',
      boxShadow: 'none',
    },
  })

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 0 }}>
        <Typography component="label" htmlFor={urlId} sx={SECTION_LABEL_SX}>
          Video Source{' '}
          <Box component="span" sx={{ color: 'error.main' }}>
            *
          </Box>
        </Typography>
        <Typography sx={HELPER_SX} data-testid="video-source-helper">
          Add a video link (YouTube, Vimeo, or direct link) or upload a video. Only one video is allowed.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            border: '1px solid #e4e9f0',
            borderRadius: '10px',
            overflow: 'hidden',
            height: 38,
            mt: 1,
          }}
        >
          <Button
            type="button"
            disableElevation
            onClick={() => applySourceKind('link')}
            data-testid="video-source-tab-link"
            sx={tabSx(d.sourceKind === 'link')}
          >
            Add Video Link
          </Button>
          <Button
            type="button"
            disableElevation
            onClick={() => applySourceKind('upload')}
            data-testid="video-source-tab-upload"
            sx={tabSx(d.sourceKind === 'upload')}
          >
            Upload Video
          </Button>
        </Box>
      </Box>

      {d.sourceKind === 'link' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3, mb: 0 }}>
          <Typography component="label" htmlFor={urlId} sx={SECTION_LABEL_SX}>
            Video URL{' '}
            <Box component="span" sx={{ color: 'error.main' }}>
              *
            </Box>
          </Typography>
          <TextField
            id={urlId}
            fullWidth
            hiddenLabel
            placeholder="Paste YouTube, Vimeo, or direct video link here"
            value={d.videoUrl}
            onChange={(e) => onLinkChange(e.target.value)}
            error={Boolean(sourceErr)}
            inputProps={{ 'data-testid': 'video-source-url' }}
            sx={OUTLINED_SX}
          />
          {sourceErr ? (
            <FormHelperText error sx={{ mx: 0 }} data-testid="video-source-error">
              {sourceErr}
            </FormHelperText>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" data-testid="video-url-supported-hint">
              <Box
                component="img"
                src={videoUrlInfoIcon}
                alt=""
                sx={{ width: 14, height: 14, display: 'block', flexShrink: 0 }}
              />
              <Typography sx={{ ...HELPER_SX, fontSize: '12px', lineHeight: 'normal' }}>
                Supported: YouTube, Vimeo, and direct MP4 links
              </Typography>
            </Stack>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: 3, mb: 0 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,.mp4"
            hidden
            data-testid="video-upload-input"
            onChange={(e) => {
              void onPickFile(e.target.files?.[0])
            }}
          />
          <Button
            type="button"
            variant="outlined"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="video-upload-button"
            sx={{ textTransform: 'none', borderRadius: '10px', mb: 1 }}
          >
            {isUploading ? 'Uploading…' : d.uploadedFileName || 'Choose MP4 file'}
          </Button>
          {uploadError ? (
            <FormHelperText error sx={{ mx: 0 }} data-testid="video-upload-error">
              {uploadError}
            </FormHelperText>
          ) : null}
          {sourceErr && !uploadError ? (
            <FormHelperText error sx={{ mx: 0 }} data-testid="video-source-error">
              {sourceErr}
            </FormHelperText>
          ) : null}
        </Box>
      )}

      <Dialog
        open={replaceOpen}
        onClose={() => {
          setReplaceOpen(false)
          setPendingKind(null)
        }}
        data-testid="video-replace-source-dialog"
      >
        <DialogTitle>Replace video source?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            Adding a new source will replace the existing video for this question.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReplaceOpen(false)
              setPendingKind(null)
            }}
            data-testid="video-replace-cancel"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmReplace}
            data-testid="video-replace-confirm"
            sx={{ textTransform: 'none' }}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export function VideoPreviewCard({
  videoUrl,
  children,
}: Readonly<{ videoUrl: string; children?: ReactNode }>) {
  return (
    <Box sx={PREVIEW_CARD_SX} data-testid="video-preview-panel">
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '20px',
          letterSpacing: '-0.15px',
          color: 'text.primary',
          mb: 2,
        }}
      >
        Video Preview
      </Typography>
      <VideoPlaybackEmbed videoUrl={videoUrl} />
      {children}
    </Box>
  )
}

export function VideoGuidelinesPanels() {
  return (
    <Stack spacing={2} data-testid="video-info-panels">
      <Box
        sx={{
          borderRadius: '10px',
          bgcolor: '#e7e6fb',
          px: '25px',
          pt: '18px',
          pb: '20px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '-0.31px',
            color: '#7e22ce',
            mb: 2,
          }}
        >
          Supported Video Sources
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: '21px',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px',
            '& li': {
              fontSize: '14px',
              lineHeight: '21px',
              fontWeight: 500,
              letterSpacing: '-0.15px',
              color: 'text.primary',
            },
          }}
        >
          <li>YouTube (youtube.com/watch?v=...)</li>
          <li>Vimeo (vimeo.com/...)</li>
          <li>Direct MP4 video links</li>
        </Box>
        <Typography sx={{ ...HELPER_SX, mt: 1.5 }}>
          The video will be played within Brushfire. Respondents will not be redirected to external
          sites.
        </Typography>
      </Box>
      <Box
        sx={{
          borderRadius: '10px',
          bgcolor: '#eaf3ff',
          px: '25px',
          pt: '18px',
          pb: '20px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '-0.31px',
            color: palettePrimaryMain,
            mb: 2,
          }}
        >
          Video Guidelines
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: '21px',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px',
            '& li': {
              fontSize: '14px',
              lineHeight: '21px',
              fontWeight: 500,
              letterSpacing: '-0.15px',
              color: 'text.primary',
            },
          }}
        >
          <li>Recommended length: 1-3 minutes</li>
          <li>Maximum file size (upload): 200MB</li>
          <li>Support formats (upload): MP4 (H.264)</li>
          <li>Maximum video allowed for a question: 1</li>
          <li>Maximum upload attempts: 3</li>
        </Box>
      </Box>
    </Stack>
  )
}
